/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ActionContext, Module } from 'vuex';
import store from '@/store';

import type RootStateType from '@/store/types';
import generateMutations, { UPDATE_STATE_TYPE } from '@/store/generate-mutations';
import { getGalileoAb } from '@/shared/galileoAb';
import { ABResEnum } from '@/enums/abExperiment';
import { CouponTypeEnum } from '@/api/coupon/types';
import { AdjustTypeEnum } from '@/dict/quotaIncrease-type';
import type { DiscountModuleStateType } from './types';
import { supportCalcDiscountCouponTypes } from './product';

const moduleStore: Module<DiscountModuleStateType, RootStateType> = {
  state: {
    /** 优惠模块优化实验结果 */
    discountModuleExperimentRes: null,
    /** 优惠模块临时额度优化实验结果 */
    discountModuleTempAmountExperimentRes: null,
  },

  getters: {
    /** 是否展示优惠模块优化，条件为实验结果为true且优惠券数量大于0 */
    isDiscountModuleExperiment(state: DiscountModuleStateType): boolean {
      const { couponList } = store.state.applyProduct;
      return state.discountModuleExperimentRes !== null
        && state.discountModuleExperimentRes === ABResEnum.HINT
        && couponList.length > 0
        && couponList.some(
          (item) => supportCalcDiscountCouponTypes.includes(item.freeCouponType as CouponTypeEnum),
        );
    },
    /** 是否展示优惠模块临时额度优化实验 */
    isDiscountModuleTempAmountExperiment(state: DiscountModuleStateType): boolean {
      return state.discountModuleTempAmountExperimentRes === ABResEnum.HINT;
    },
    /** 是否在优惠模块中展示临时额度（实验命中 + 有临额） */
    showTempAmountInDiscountModule(
      state: DiscountModuleStateType,
      getters: { isDiscountModuleTempAmountExperiment: boolean },
    ): boolean {
      const { channel } = store.state.applyProduct;
      const { adjustQuota } = channel || {};
      return getters.isDiscountModuleTempAmountExperiment
        && adjustQuota?.adjustType === AdjustTypeEnum.TEMP
        && Number(adjustQuota?.adjustValue) > 0;
    },
  },

  actions: {
    /**
     * 伽利略实验：https://ab.hellobike.cn/experiment/detail/202509291046027719?isDetail=true
     * 实验名称：提现页优惠模块优化实验
     * 实验目的：测试提现页优惠模块优化实验分流结果
     */
    async getDiscountModuleExperimentRes(
      context: ActionContext<DiscountModuleStateType, RootStateType>,
    ) {
      const { couponList } = store.state.applyProduct;

      /** 未设置过实验结果，且优惠券数量大于0，则获取实验结果 */
      if (context.state.discountModuleExperimentRes === null
        && couponList.length > 0
        && couponList.some(
          (item) => supportCalcDiscountCouponTypes.includes(item.freeCouponType as CouponTypeEnum),
        )
      ) {
        const { result } = await getGalileoAb().getAbResCatchError('202509291046027719', '202509291048578595');
        if (result) {
          context.dispatch(UPDATE_STATE_TYPE, {
            discountModuleExperimentRes: result,
          });
        }
      }
      context.dispatch('getDiscountModuleTempAmountExperimentRes');
    },
    /**
     * 伽利略实验：https://ab.hellobike.cn/experiment/detail/202511041421195748?isDetail=true
     * 实验名称：提现页优惠模块临时额度优化实验
     * 实验目的：测试提现页优惠模块临时额度优化实验分流结果
     */
    async getDiscountModuleTempAmountExperimentRes(
      context: ActionContext<DiscountModuleStateType, RootStateType>,
    ) {
      const { channel } = store.state.applyProduct;
      /** 调用实验条件
       * 1. 优惠模块优化实验为实验组
       * 2. 有可用的自营临时额度
       * 3. 未获取过临时额度优化实验结果
       */
      if (
        context.state.discountModuleExperimentRes === ABResEnum.HINT
        && channel?.adjustQuota?.adjustType === AdjustTypeEnum.TEMP
        && channel?.adjustQuota?.adjustValue > 0
        && context.state.discountModuleTempAmountExperimentRes === null
      ) {
        const { result } = await getGalileoAb().getAbResCatchError('202511041421195748', '202511041421195834');
        if (result) {
          context.dispatch(UPDATE_STATE_TYPE, {
            discountModuleTempAmountExperimentRes: result,
          });
        }
      }
    },
  },
};

export default generateMutations(moduleStore);
